import { useState, useEffect, useRef } from 'react'
import { listStyles, type StyleSummary } from '../../../../api/styles'
import { VISUAL_STYLES } from '../../../../constants/styles'

/**
 * Map hardcoded VISUAL_STYLES to StyleSummary shape for fallback.
 */
function fallbackStyles(): StyleSummary[] {
  return VISUAL_STYLES.map((s) => ({
    id: s.value,
    name: s.label,
    prompt: s.prompt,
    use_reference_in_generation: false,
    reference_image_count: 0,
    tags: [...s.tags],
    thumbnail_url: null,
    preview_image_urls: [],
  }))
}

export interface UseStyleLibraryResult {
  styles: StyleSummary[]
  isLoading: boolean
  error: string | null
}

/**
 * Shared hook that fetches available styles from the API.
 *
 * @param enabled - When true, triggers a fetch. Re-fetches each time
 *   `enabled` transitions from false to true (e.g. when a modal opens).
 *   Falls back to hardcoded VISUAL_STYLES on API error.
 */
export function useStyleLibrary(enabled: boolean = true): UseStyleLibraryResult {
  const [styles, setStyles] = useState<StyleSummary[]>(fallbackStyles())
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Track whether we have successfully fetched from the API at least once
  const succeededRef = useRef(false)

  useEffect(() => {
    if (!enabled) return
    // If we already fetched successfully, no need to re-fetch
    if (succeededRef.current) return

    let cancelled = false
    setIsLoading(true)

    listStyles()
      .then((res) => {
        if (!cancelled) {
          setStyles(res.styles)
          setError(null)
          succeededRef.current = true
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setStyles(fallbackStyles())
          setError(err.message || 'Failed to load styles')
          // Do NOT set succeededRef — next time enabled becomes true, we retry
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [enabled])

  return { styles, isLoading, error }
}

export { fallbackStyles }
